import { ErrorMessage } from "@hookform/error-message";
import { ExcessLiabilityPolicy } from "pmt-types/src/models/ExcessLiabilityPolicy.ts";
import { GeneralLiabilityPolicy } from "pmt-types/src/models/GeneralLiabilityPolicy.ts";
import { Source } from "pmt-types/src/models/Source.ts";
import { Controller, ControllerRenderProps } from "react-hook-form";
import cx from "../../utils/cx.ts";
import ErrorLabel from "./errorLabel.tsx";
import TabularInputs from "./tabularInputs.tsx";
import UncontrolledTabbedCheckbox from "./uncontrolledTabbedCheckbox.tsx";

type CoverageTableRowDef = {
  type: string;
  policyNumber: string;
  componentFn: (field: ControllerRenderProps) => JSX.Element;
};

type Props = {
  name: string;
  source: Source;
};

export default function CoveragePicker({ name, source }: Props) {
  const isExcessLiabilityIncluded = (
    excessLiabilities: ExcessLiabilityPolicy[],
    excessLiabilityToCheck: ExcessLiabilityPolicy,
  ): boolean => {
    return excessLiabilities.some(
      (e: ExcessLiabilityPolicy) =>
        e.policyNumber === excessLiabilityToCheck.policyNumber,
    );
  };

  const buildInputForGeneralLiability = (
    generalLiability: GeneralLiabilityPolicy,
    field: ControllerRenderProps,
  ): JSX.Element => {
    return (
      <UncontrolledTabbedCheckbox
        isSelected={field.value.generalLiability != null}
        onChecked={() =>
          field.onChange({
            ...field.value,
            generalLiability: generalLiability,
          })
        }
        onUnchecked={() =>
          field.onChange({
            ...field.value,
            generalLiability: undefined,
          })
        }
      />
    );
  };

  const buildInputForExcessLiability = (
    excessLiability: ExcessLiabilityPolicy,
    field: ControllerRenderProps,
  ): JSX.Element => {
    return (
      <UncontrolledTabbedCheckbox
        isSelected={isExcessLiabilityIncluded(
          field.value.excessLiabilities,
          excessLiability,
        )}
        onChecked={() =>
          field.onChange({
            ...field.value,
            excessLiabilities: [
              ...field.value.excessLiabilities,
              excessLiability,
            ],
          })
        }
        onUnchecked={() =>
          field.onChange({
            ...field.value,
            excessLiabilities: field.value.excessLiabilities.filter(
              (e: ExcessLiabilityPolicy) =>
                e.policyNumber !== excessLiability.policyNumber,
            ),
          })
        }
      />
    );
  };

  const coverageTableRows: CoverageTableRowDef[] = [
    {
      type: "General Liability",
      policyNumber: source.generalLiability.policyNumber,
      componentFn: (field: ControllerRenderProps) =>
        buildInputForGeneralLiability(source.generalLiability, field),
    },
    ...(source.excessLiabilities ?? []).map((excessLiability) => ({
      type: "Excess Liability",
      policyNumber: excessLiability.policyNumber,
      componentFn: (field: ControllerRenderProps) =>
        buildInputForExcessLiability(excessLiability, field),
    })),
  ];

  return (
    <>
      <Controller
        name={name}
        render={({ field }) => (
          <TabularInputs
            label={"Coverages"}
            tableDef={[
              { header: "Type", component: (row) => row.type },
              { header: "Policy #", component: (row) => row.policyNumber },
              {
                header: "Include in Certificate",
                component: (row) => row.componentFn(field),
              },
            ]}
            rows={coverageTableRows}
          />
        )}
      />
      <ErrorMessage
        name={name}
        render={({ message }) => <ErrorLabel message={message} />}
      />
    </>
  );

  return (
    <>
      <Controller
        name={name}
        render={({ field }) => (
          <div className="isolate flex divide-x divide-gray-200 rounded-lg shadow shadow-gray-400">
            {source.excessLiabilities != null &&
              source.excessLiabilities.map(
                (excessLiability, excessLiabilityIndex) => (
                  <div
                    key={excessLiability.policyNumber}
                    className={cx(
                      isExcessLiabilityIncluded(
                        field.value?.excessLiabilities,
                        excessLiability,
                      )
                        ? "text-gray-900 bg-sky-50"
                        : "text-gray-500 bg-white hover:text-gray-700",
                      excessLiabilityIndex === 0 ? "rounded-l-lg" : "",
                      source.excessLiabilities != null &&
                        excessLiabilityIndex ===
                          source.excessLiabilities.length - 1
                        ? "rounded-r-lg"
                        : "",
                      "group relative min-w-0 flex-1 overflow-hidden py-2 px-4 text-center text-sm font-medium hover:bg-sky-50 focus:z-10 cursor-pointer",
                    )}
                    onClick={() =>
                      isExcessLiabilityIncluded(
                        field.value?.excessLiabilities,
                        excessLiability,
                      )
                        ? field.onChange(
                            field.value.excessLiabilities.filter(
                              (e: ExcessLiabilityPolicy) =>
                                e.policyNumber === excessLiability.policyNumber,
                            ),
                          )
                        : field.onChange([
                            ...(field.value.excessLiabilities ?? []),
                            excessLiability,
                          ])
                    }
                  >
                    <span>Include</span>
                    <span
                      aria-hidden="true"
                      className={cx(
                        isExcessLiabilityIncluded(
                          field.value?.excessLiabilities,
                          excessLiability,
                        )
                          ? "bg-sky-500"
                          : "bg-transparent",
                        "absolute inset-x-0 bottom-0 h-0.5",
                      )}
                    />
                  </div>
                ),
              )}
          </div>
        )}
      />
      <ErrorMessage
        name={name}
        render={({ message }) => <ErrorLabel message={message} />}
      />
    </>
  );
}
